/**
 * The view-model for one search result row (search-result-disclosure 03).
 *
 * `SearchResult` already carries the full item/container, so this is a pure
 * projection: media URLs from the preview endpoints, the placement-derived
 * owner/tags/type for the facet layer, and "what's inside" for a container.
 * No fetches, no originals — a result row only ever asks for downscaled
 * previews (ADR-0008/ADR-0031).
 */

import {
  pathLabelOf,
  type Container,
  type ContainerItem,
  type Item,
  type Properties,
  type SearchResult,
} from "@egalregal/shared";
import { requests } from "../../shared/api";
import type { FacetHit } from "./facets";

export interface HitPhoto {
  key: string;
  url: string;
  label: string;
}

export interface HitContents {
  name: string;
  quantity: number;
}

/** One readable container holding an item, with a breadcrumb-style path. */
export interface HitPlacement {
  containerId: string;
  name: string;
  path: string;
}

export interface SearchHit extends FacetHit {
  result: SearchResult;
  /** Stable row identity: one deduped item row per best placement. */
  key: string;
  name: string;
  /** Breadcrumb line; items append their own name, per the existing row. */
  trail: string;
  description: string;
  photos: HitPhoto[];
  contents: HitContents[];
  properties?: Properties;
  /** An item with no placement has no container to open. */
  noPlacement: boolean;
  /** Every readable placement of the item; one entry for single placement. */
  placements: HitPlacement[];
  /** Quantity at the primary (best) placement, when the mirror knows it. */
  quantity?: number;
}

/**
 * Project the ranked results onto rows, deriving placement ownership and
 * container contents from the catalog and item mirror.
 *
 * @param results ranked search results.
 * @param containers the readable catalog union.
 * @param items the mirrored item universe (for contents names).
 * @param placements the mirrored placement links.
 */
export function toSearchHits(
  results: readonly SearchResult[],
  containers: readonly Container[],
  items: readonly Item[],
  placements: readonly ContainerItem[],
): SearchHit[] {
  const byId = new Map(containers.map((container) => [container.id, container]));
  const itemNames = new Map(items.map((item) => [item.id, item.name]));
  const contents = contentsByContainer(placements, itemNames);
  const placementsByItem = new Map<string, ContainerItem[]>();
  for (const placement of placements) {
    const list = placementsByItem.get(placement.itemId) ?? [];
    list.push(placement);
    placementsByItem.set(placement.itemId, list);
  }

  // One row per item across placements (ticket 11): the first occurrence is the
  // best-ranked placement, so its trail/identity wins and the rest move into the
  // row's placement list. Container hits are untouched.
  const seenItems = new Set<string>();
  const deduped = results.filter((result) => {
    if (result.type === "container") return true;
    if (seenItems.has(result.item.id)) return false;
    seenItems.add(result.item.id);
    return true;
  });

  return deduped.map((result) => {
    if (result.type === "container") {
      const container = result.container;
      return {
        result,
        key: `container:${container.id}`,
        name: container.name,
        trail: result.path,
        description: container.description,
        owner: container.owner ?? "",
        tags: container.tags,
        type: container.kind,
        photos: container.files
          .filter((file) => file.kind === "photo")
          .map((file) => ({
            key: file.id,
            url: requests.media.previewUrl(container.id, file.id),
            label: file.name,
          })),
        contents: contents.get(container.id) ?? [],
        properties: container.properties,
        noPlacement: false,
        placements: [],
      };
    }

    const item = result.item;
    const host = result.containerId ? byId.get(result.containerId) : undefined;
    const localPhotos = item.photos.map((photo) => ({
      key: photo.id,
      url: requests.media.itemPhotoPreviewUrl(item.id, photo.id),
      label: `${item.name} · ${photo.type}`,
    }));
    // A provider cover (Google Books) is the only external URL a row may show;
    // local previews always win.
    const photos =
      localPhotos.length > 0
        ? localPhotos
        : item.photo
          ? [{ key: "external", url: item.photo, label: item.name }]
          : [];
    const byContainer = new Map<string, HitPlacement>();
    for (const placement of placementsByItem.get(item.id) ?? []) {
      const host = byId.get(placement.containerId);
      const name =
        host?.name ??
        (placement.containerId === result.containerId ? result.containerName : placement.containerId);
      byContainer.set(placement.containerId, {
        containerId: placement.containerId,
        name,
        path: host
          ? pathLabelOf(containers, host.id)
          : placement.containerId === result.containerId
            ? result.path
            : placement.containerId,
      });
    }
    // The result's own placement is authoritative even before the mirror loads.
    if (result.containerId && !byContainer.has(result.containerId)) {
      byContainer.set(result.containerId, {
        containerId: result.containerId,
        name: result.containerName,
        path: result.path,
      });
    }
    const itemPlacements = result.containerId
      ? [...byContainer.values()].sort((left, right) => left.path.localeCompare(right.path))
      : [];
    const primaryQuantity = placementsByItem
      .get(item.id)
      ?.find((placement) => placement.containerId === result.containerId)?.quantity;
    return {
      result,
      key: `item:${item.id}:${result.containerId}`,
      name: item.name,
      trail: result.path ? `${result.path} › ${item.name}` : "",
      description: item.description,
      // ADR-0022 amendment: owner/tags are inherited from the placement host.
      owner: host?.owner ?? "",
      tags: host?.tags ?? [],
      type: item.category,
      photos,
      contents: [],
      properties: item.properties,
      noPlacement: !result.containerId,
      placements: itemPlacements,
      ...(primaryQuantity !== undefined ? { quantity: primaryQuantity } : {}),
    };
  });
}

/**
 * The UUID shown on levels 2/3. For a container hit and a placed item that is
 * the host container — the thing the searcher walks to and the unit ADR-0027
 * assigns pictograms to. An unplaced item has no container, so its own id is the
 * only handle.
 */
export function identityUuidOf(hit: SearchHit): string {
  if (hit.result.type === "container") return hit.result.container.id;
  // Only the box has a user-meaningful identity; an unplaced item's UUID is a
  // persistence detail and must not be shown.
  return hit.result.containerId;
}

/** The label name for the identity line: the same entity as identityUuidOf. */
export function identityNameOf(hit: SearchHit): string {
  if (hit.result.type === "container") return hit.result.container.name;
  return hit.result.containerName;
}

/** Placements grouped by container, named from the item mirror. */
function contentsByContainer(
  placements: readonly ContainerItem[],
  itemNames: ReadonlyMap<string, string>,
): Map<string, HitContents[]> {
  const byContainer = new Map<string, HitContents[]>();
  for (const placement of placements) {
    const name = itemNames.get(placement.itemId);
    if (!name) continue;
    const list = byContainer.get(placement.containerId) ?? [];
    list.push({ name, quantity: placement.quantity });
    byContainer.set(placement.containerId, list);
  }
  for (const list of byContainer.values()) {
    list.sort((left, right) => left.name.localeCompare(right.name));
  }
  return byContainer;
}
